import { type ContactType } from "@/lib/types/Contact";

export const GENDERS = ["Male", "Female", "Non-Binary"] as const;
export const LANGUAGES = [
  "Arabic",
  "Chinese",
  "English",
  "Farsi",
  "French",
  "Spanish",
  "Tagalog",
  "Vietnamese",
] as const;

export const CONTACT_INDIVIDUAL_TIMEZONES = [
  "Eastern (ET)",
  "Central (CT)",
  "Mountain (MT)",
  "Pacific (PT)",
  "Alaska (AKT)",
  "Hawaii (HT)",
] as const;

export const formatContactIndividualTimezoneLabel = (timezone: string) =>
  timezone;

export const CONTACT_TYPES = ["individual", "organization", "group"] as const;

export const TITLES = [
  "Mr.",
  "Mrs.",
  "Ms.",
  "Mx.",
  "Dr.",
  "Prof.",
  "Judge",
  "Esq.",
] as const;

export const RACES = [
  "American Indian or Alaska Native",
  "Asian",
  "Black or African American",
  "Hispanic or Latino",
  "Native Hawaiian or Other Pacific Islander",
  "White",
] as const;

export const CONTACT_INDIVIDUAL_SUBTYPES = [
  "Attorney",
  "Client",
  "Defendant",
  "Expert Witness",
  "Insurance Adjuster",
  "Lien Representative",
  "Medical Professional",
  "Witness",
] as const;

export const CONTACT_INDIVIDUAL_SUBTYPES_WITH_ADDITIONAL_FIELDS = [
  "Client",
  "Defendant",
] as const;

export const CONTACT_ORGANIZATION_SUBTYPES = [
  "Insurance",
  "Police Department",
  "Medical Provider",
  "Record Retrieval Service",
  "Law Firm",
  "Auto Shop",
] as const;

export const CONTACT_ORGANIZATION_MEDICAL_PROVIDER_SPECIALIZATIONS = [
  "Allergy",
  "Ambulance",
  "Anesthesiology",
  "Audiology",
  "Autopsy",
  "Cardiology",
  "Chiropractic",
  "Cognitive Therapy",
  "Counseling",
  "Dentistry",
  "Dermatology",
  "Dialysis",
  "Emergency Room",
  "Endocrinology",
  "ENT",
  "ER Physicians",
  "Eye Care",
  "Family Practice",
  "Fire Department",
  "Gastroenterology",
  "General Practice",
  "Geriatrics",
  "Gynecology",
  "Hand Specialty",
  "Hematology",
  "Home Care",
  "Hospital",
  "Immunology",
  "Infectious Diseases",
  "Internal Medicine",
  "Laboratory",
  "Massage Therapy",
  "Medical Billing",
  "Medical Devices",
  "Medical Transport",
  "MRI Imaging",
  "Nephrology",
  "Neurology",
  "Neuropathy",
  "Neuropsychiatry",
  "Neuropsychology",
  "Neurosurgery",
  "NICU",
  "Nursing Home",
  "Obstetrics",
  "Occupational Therapy",
  "Oncology",
  "Ophthalmology",
  "Optometry",
  "Orthopedic",
  "Osteopathy",
  "Pain Management",
  "Pathology",
  "Pediatric",
  "Pharmacy",
  "Physiatry",
  "Physical Therapy",
  "Plastic Surgery",
  "Podiatry",
  "Primary Care Provider",
  "Prosthetics",
  "Psychiatry",
  "Psychology",
  "Pulmonary",
  "Radiology",
  "Rheumatology",
  "Skilled Nurse",
  "Speech Therapy",
  "Sports Medicine",
  "Surgery",
  "Therapy",
  "Trauma Physician",
  "Urgent Care",
  "Urology",
  "Vascular Surgery",
  "Vocal Therapy",
  "Vocational Therapy",
  "Wound Care",
] as const;

export const CONTACT_INDIVIDUAL_EXPERT_WITNESS_SPECIALIZATIONS = [
  "Accident Reconstruction",
  "Air Quality",
  "Animal Accidents",
  "Aquatics",
  "ATV Accidents",
  "Aviation Accidents",
  "Bicycle Accidents",
  "Biomechanics",
  "Boating Accidents",
  "Car Accidents",
  "Carbon Monoxide",
  "Cell Phone",
  "Chemical Accidents",
  "Childcare Expert",
  "Chiropractic",
  "Commercial Driver Licenses",
  "Dentistry",
  "Department of Transportation",
  "Disabilities",
  "DME",
  "Dog Trainer",
  "Economist",
  "Electrical Accidents",
  "Emergency Response",
  "Engineering",
  "Financial",
  "Firearms",
  "Fire & Explosions",
  "Health Care Industry",
  "Highway Defects",
  "Hospital Industry",
  "Industrial Accidents",
  "Insurance Bad Faith",
  "Insurance Coverage",
  "Insurance Industry Practices",
  "Insurance Risk Management",
  "Law Enforcement Excessive Force",
  "Law Enforcement Industry Practices",
  "Life Care Planning",
  "Lighting & Visibility",
  "Machinery",
  "Manufacturing",
  "Medical",
  "Medical Billing",
  "Medical Examiner",
  "Mental Health",
  "Motorcycle Accidents",
  "MVA Accidents",
  "Neurology",
  "Neuropsychiatrist",
  "Neuropsychologist",
  "Neurosurgery",
  "Nursing",
  "Oil & Gas",
  "Orthopedic",
  "Other",
  "Pain Management",
  "Pathology",
  "Pedestrian Accidents",
  "Pediatrics",
  "Pharmacology",
  "Physiatry",
  "Police Pursuit",
  "Premises Liability",
  "Psychiatry",
  "Psychology",
  "Security",
  "Sleep & Fatigue",
  "Slip & Fall Accidents",
  "Social Worker",
  "Toxicology",
  "Traffic Offenses",
  "Train Accidents",
  "Tree & Landscaping",
  "Vehicle Defect",
  "Vocational Rehabilitation",
  "Weather",
  "Workers Compensation",
] as const;

export const CONTACT_ADDRESS_LABELS = ["Home", "Work", "Other"] as const;

export const CONTACT_EMAIL_LABELS = ["Personal", "Work", "Other"] as const;

export const CONTACT_FAX_LABELS = ["Personal", "Work", "Other"] as const;

export const CONTACT_PHONE_LABELS = [
  "Home",
  "Work",
  "Mobile",
  "Other",
] as const;

export const CONTACT_WEBSITE_LABELS = ["Personal", "Work", "Other"] as const;

export const CONTACT_MEDICAL_PROVIDER_COMMUNICATION_LABELS = [
  "Billing Record Requests",
  "Medical Record Requests",
  "Billing and Medical Record Requests",
] as const;

const isMedicalProviderOrganization = (
  type?: ContactType | null,
  organizationSubTypes?: readonly string[] | null,
) =>
  type === "organization" && organizationSubTypes?.includes("Medical Provider");

const appendMedicalProviderLabels = (labels: readonly string[]) => [
  ...labels,
  ...CONTACT_MEDICAL_PROVIDER_COMMUNICATION_LABELS,
];

export const getContactEmailLabelOptions = (
  type?: ContactType | null,
  organizationSubTypes?: readonly string[] | null,
) => {
  const baseLabels = [...CONTACT_EMAIL_LABELS];
  if (isMedicalProviderOrganization(type, organizationSubTypes)) {
    return appendMedicalProviderLabels(baseLabels);
  }
  return baseLabels;
};

export const getContactPhoneLabelOptions = (
  type?: ContactType | null,
  organizationSubTypes?: readonly string[] | null,
) => {
  const baseLabels = [...CONTACT_PHONE_LABELS];
  if (isMedicalProviderOrganization(type, organizationSubTypes)) {
    return appendMedicalProviderLabels(baseLabels);
  }
  return baseLabels;
};

// Communication statuses for contact messages (SMS/WhatsApp)
export const CONTACT_COMMUNICATION_STATUSES = [
  "queued",
  "delivered",
  "undelivered",
  "failed",
  "received",
  "read",
] as const;
